/**
 * Subscription — tenant-side page for Stripe subscription management.
 *
 * Shows current package, subscription_status, and a "Subscribe / Change plan"
 * card with each Stripe-synced master_package. Polls /billing/subscriptions/checkout-status
 * after Stripe redirect to flip the badge to active.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Loader2, CheckCircle2, ArrowRight, ExternalLink, Crown } from "lucide-react";
import Sidebar from "../components/Sidebar";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  none:      { bg: "#E5E7EB", text: "#374151", label: "No plan" },
  active:    { bg: "#D1FAE5", text: "#047857", label: "Active" },
  trialing:  { bg: "#DBEAFE", text: "#1E40AF", label: "Trialing" },
  past_due:  { bg: "#FEF3C7", text: "#92400E", label: "Past due" },
  cancelled: { bg: "#FEE2E2", text: "#B91C1C", label: "Cancelled" },
  canceled:  { bg: "#FEE2E2", text: "#B91C1C", label: "Cancelled" },
};

export default function Subscription() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [status, setStatus] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyPkgId, setBusyPkgId] = useState("");
  const [pollMsg, setPollMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/billing/subscriptions/status`),
        axios.get(`${API}/master/packages`).catch(() => ({ data: { packages: [] } })),
      ]);
      setStatus(s.data || {});
      setPackages(p.data.packages || []);
    } catch (err) {
      // Master packages list may 403 for non-master users — that branch is
      // already handled by the inner `.catch` on line 40, so reaching here
      // means `subscriptions/status` itself failed. Surface it.
      console.error("Subscription load failed:", err);
      toast.error(err?.response?.data?.detail || err?.message || "Failed to load subscription");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // After return from Stripe — poll for confirmation.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (sid) {
      setPollMsg(isPt ? "Confirmando assinatura…" : "Confirming subscription…");
      let attempts = 0;
      const tick = async () => {
        attempts += 1;
        try {
          const { data } = await axios.get(`${API}/billing/subscriptions/checkout-status/${sid}`);
          if (data.payment_status === "paid") {
            toast.success(isPt ? "Assinatura ativada!" : "Subscription activated!");
            setPollMsg("");
            window.history.replaceState({}, "", "/subscription");
            load();
            return;
          }
          if (data.status === "expired" || attempts >= 6) {
            setPollMsg("");
            return;
          }
          setTimeout(tick, 2000);
        } catch (_e) {
          setPollMsg("");
        }
      };
      tick();
    } else if (params.get("cancelled")) {
      toast.info(isPt ? "Checkout cancelado" : "Checkout cancelled");
      window.history.replaceState({}, "", "/subscription");
    }
    // eslint-disable-next-line
  }, []);

  const subscribablePackages = useMemo(
    () => packages.filter((p) => p.stripe_price_id && p.monthly_price_usd > 0),
    [packages],
  );

  const subscribe = async (pkgId) => {
    setBusyPkgId(pkgId);
    try {
      const { data } = await axios.post(`${API}/billing/subscriptions/checkout`, {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isPt ? "Falha ao iniciar checkout" : "Checkout failed"));
    } finally { setBusyPkgId(""); }
  };

  const openPortal = async () => {
    try {
      const { data } = await axios.post(`${API}/billing/subscriptions/portal`, {
        return_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portal failed");
    }
  };

  const badge = STATUS_BADGE[status?.subscription_status] || STATUS_BADGE.none;
  const currentPkg = packages.find((p) => p.id === status?.package_id);

  return (
    <div className="flex min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
      <Sidebar />
      <main className="flex-1 ml-[var(--al-sidebar-w,260px)] p-6 lg:p-10 max-w-5xl" data-testid="subscription-page">
        <header className="mb-8">
          <h1 className="text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <CreditCard className="w-7 h-7 text-[#635BFF]" />
            {isPt ? "Assinatura" : "Subscription"}
          </h1>
          <p className="text-[13px] text-[var(--al-text-muted)] mt-2">
            {isPt
              ? "Gerencie seu plano AgenteLead Pro com cobrança recorrente via Stripe."
              : "Manage your AgenteLead Pro plan with Stripe recurring billing."}
          </p>
        </header>

        {pollMsg && (
          <div className="mb-6 al-card p-4 flex items-center gap-2 text-[13px]">
            <Loader2 className="w-4 h-4 animate-spin text-[#635BFF]" /> {pollMsg}
          </div>
        )}

        <section className="al-card p-6 mb-6" data-testid="subscription-current">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-[var(--al-text-muted)] mb-1">{isPt ? "Status atual" : "Current status"}</div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[11px] font-bold px-2 py-0.5 rounded"
                      data-testid="subscription-status-badge"
                      style={{ background: badge.bg, color: badge.text }}>
                  {badge.label}
                </span>
                {currentPkg && (
                  <span className="font-display text-[18px] font-bold">{currentPkg.name}</span>
                )}
              </div>
              {status?.current_period_end && (
                <div className="text-[12px] text-[var(--al-text-muted)]">
                  {isPt ? "Renova em" : "Renews on"}: {new Date(status.current_period_end).toLocaleDateString()}
                </div>
              )}
            </div>
            {status?.stripe_customer_id && (
              <button onClick={openPortal}
                data-testid="subscription-portal-btn"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-md border text-[12px] font-semibold hover:bg-[var(--al-bg-soft)]">
                {isPt ? "Gerenciar pagamento" : "Manage billing"} <ExternalLink className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </section>

        <h2 className="text-[18px] font-extrabold mb-4">{isPt ? "Planos disponíveis" : "Available plans"}</h2>
        {loading ? (
          <div className="flex items-center gap-2 text-[var(--al-text-muted)] text-[13px]">
            <Loader2 className="w-4 h-4 animate-spin" /> {isPt ? "Carregando…" : "Loading…"}
          </div>
        ) : subscribablePackages.length === 0 ? (
          <div className="al-card p-8 text-center text-[var(--al-text-muted)]">
            {isPt
              ? "Nenhum plano sincronizado com Stripe ainda. Peça ao Master para configurar os preços."
              : "No plans synced with Stripe yet. Ask the Master to configure pricing."}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {subscribablePackages.map((pkg) => {
              const isCurrent = status?.package_id === pkg.id && status?.subscription_status === "active";
              return (
                <div key={pkg.id}
                  data-testid={`subscription-pkg-${pkg.slug}`}
                  className="al-card p-5 flex flex-col"
                  style={{ borderColor: isCurrent ? "#635BFF" : undefined, borderWidth: isCurrent ? 2 : undefined }}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display text-[20px] font-extrabold flex items-center gap-2" style={{ color: pkg.color }}>
                      {pkg.slug === "premium" && <Crown className="w-4 h-4" />}
                      {pkg.name}
                    </h3>
                    {isCurrent && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                        style={{ background: "#635BFF22", color: "#635BFF" }}>{isPt ? "Atual" : "Current"}</span>
                    )}
                  </div>
                  <div className="text-[28px] font-extrabold tracking-tight">
                    ${pkg.monthly_price_usd}
                    <span className="text-[12px] text-[var(--al-text-muted)] font-normal">/{isPt ? "mês" : "mo"}</span>
                  </div>
                  <ul className="my-4 text-[12.5px] space-y-1.5 flex-1">
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> {pkg.agents?.length || 0} {isPt ? "agentes" : "agents"}</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> {pkg.modules?.length || 0} {isPt ? "módulos" : "modules"}</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      {pkg.leads_per_month === 0 ? (isPt ? "Leads ilimitados" : "Unlimited leads") : `${pkg.leads_per_month} ${isPt ? "leads/mês" : "leads/mo"}`}
                    </li>
                  </ul>
                  <button onClick={() => subscribe(pkg.id)} disabled={isCurrent || busyPkgId === pkg.id}
                    data-testid={`subscription-checkout-${pkg.slug}`}
                    className="w-full h-10 rounded-md bg-[#635BFF] hover:bg-[#5048DC] text-white font-bold text-[13px] inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                    {busyPkgId === pkg.id ? <Loader2 className="w-4 h-4 animate-spin" /> :
                      isCurrent ? (isPt ? "Plano atual" : "Current plan") : (
                        <>{isPt ? "Assinar" : "Subscribe"} <ArrowRight className="w-3.5 h-3.5" /></>
                      )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
